import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Active,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type Over,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { canWorkBoard, currentWeekKey } from '@infras/shared-types';
import { BoardCard, BoardCardBody, type LabelMode } from '../components/board/BoardCard';
import { BoardColumn } from '../components/board/BoardColumn';
import { BoardFilters, type BoardFilterValue } from '../components/board/BoardFilters';
import {
  BOARD_QUERY,
  COLUMNS,
  LABELS_QUERY,
  MOVE_MUTATION,
  UNPLANNED_PAGE,
  UNPLANNED_QUERY,
  apiColumn,
  columnTitle,
  columnOfWorkflow,
  isConflictError,
  shortWeek,
  type BoardCardData,
  type BoardData,
  type BoardLabel,
  type ColumnKey,
} from '../components/board/boardModel';
import {
  SR_INSTRUCTIONS,
  boardCollision,
  cardIdOf,
  columnKeyboardCoordinates,
  columnOf,
  isBelow,
  makeAnnouncements,
  planDrop,
  resetKeyboardJump,
  type DropPlan,
} from '../components/board/boardDnd';
import { useMe } from '../lib/useMe';
import { mutationErrorMessage } from '../lib/gqlError';
import '../styles/requests-shared.css';
import '../styles/requests-list.css';
import '../styles/board.css';

/**
 * The weekly board (/tasks/board) — engineer and above. Columns are the mutually exclusive
 * `workflow::` labels; dragging a card changes its label via moveTaskOnBoard. Into DONE
 * closes the task, out of DONE reopens it. Dragging up or down (any column, DONE too) sets the
 * card's place, shared by the team: the api gets the ids of the VISIBLE neighbours, so a
 * filtered board leaves hidden cards where they are. The move is optimistic: the card jumps to
 * its new place at once, and a refusal (someone else moved it or a neighbour meanwhile, a
 * pending access grant…) puts it back, says why, and reloads. See docs/tasks.md "The board and the weekly plan".
 */

const FILTER_KEYS = ['week', 'assignee', 'label', 'q'] as const;
const UNPLANNED_PREF = 'infras.board.unplanned.open';

/** The viewer's explicit choice, or null when they never toggled (then: open when non-empty). */
function readPref(): boolean | null {
  try {
    const v = window.localStorage.getItem(UNPLANNED_PREF);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}
function writePref(open: boolean) {
  try {
    window.localStorage.setItem(UNPLANNED_PREF, open ? '1' : '0');
  } catch {
    /* private mode / blocked storage: the toggle still works for this visit */
  }
}

const LABELS_PREF = 'infras.board.labels';

/** How card labels render, per viewer: 'compact' coloured bars (the default) or 'full' named chips. */
function readLabelMode(): LabelMode {
  try {
    return window.localStorage.getItem(LABELS_PREF) === 'full' ? 'full' : 'compact';
  } catch {
    return 'compact';
  }
}
function writeLabelMode(mode: LabelMode) {
  try {
    window.localStorage.setItem(LABELS_PREF, mode);
  } catch {
    /* blocked storage: the toggle still works for this visit */
  }
}

const EMPTY: Record<ColumnKey, string> = {
  none: 'Every open task is planned.',
  todo: 'No tasks.',
  in_progress: 'No tasks.',
  done: 'Nothing done yet.',
};

interface Toast {
  id: number;
  tone: 'ok' | 'bad';
  text: string;
}

export default function TaskBoardPage() {
  const { me, role, loading } = useMe();
  if (loading) {
    return (
      <div className="page page--wide">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (role && !canWorkBoard(role)) {
    return (
      <div className="page page--wide" data-testid="board-forbidden">
        <div className="detail-head">
          <h2>Board</h2>
        </div>
        <p className="detail-desc">The board is for engineers and above. Ask a maintainer for a role.</p>
      </div>
    );
  }
  return <Board me={me?.email.toLowerCase() ?? null} />;
}

function Board({ me }: { me: string | null }) {
  const [params, setParams] = useSearchParams();
  const filters: BoardFilterValue = {
    week: params.get('week') ?? '',
    assignee: params.get('assignee') ?? '',
    label: params.get('label') ?? '',
    q: params.get('q') ?? '',
  };
  // The week the viewer is looking at, sent with a move (the api reads it for DONE drops).
  const weekView = filters.week || null;
  const setFilters = useCallback(
    (patch: Partial<BoardFilterValue>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const k of FILTER_KEYS) {
            if (!(k in patch)) continue;
            const v = (patch[k] ?? '').trim();
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const vars = {
    week: filters.week || null,
    assignee: filters.assignee || null,
    label: filters.label || null,
    q: filters.q || null,
  };
  const board = useQuery<BoardData>(BOARD_QUERY, { variables: vars, fetchPolicy: 'cache-and-network' });
  const [unplannedPref, setUnplannedPref] = useState(readPref);
  const [labelMode, setLabelMode] = useState<LabelMode>(readLabelMode);
  const toggleLabels = useCallback(() => {
    setLabelMode((m) => {
      const next: LabelMode = m === 'full' ? 'compact' : 'full';
      writeLabelMode(next);
      return next;
    });
  }, []);
  const unplannedFilter = useMemo(
    () => ({
      state: ['open'],
      workflow: ['none'],
      assignee: vars.assignee,
      labels: vars.label ? [vars.label] : null,
      q: vars.q,
      sort: 'board',
      limit: UNPLANNED_PAGE,
      offset: 0,
    }),
    [vars.assignee, vars.label, vars.q],
  );
  const unplanned = useQuery<{
    tasks: { total: number; items: BoardCardData[] };
  }>(UNPLANNED_QUERY, {
    // Always fetched (one page): the lane shows its count even when collapsed, and opens by
    // default when it has open tasks in it.
    variables: { filter: unplannedFilter },
    fetchPolicy: 'cache-and-network',
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const { fetchMore: fetchMoreUnplanned } = unplanned;
  const loadMoreUnplanned = useCallback(async () => {
    const have = unplanned.data?.tasks.items.length ?? 0;
    setLoadingMore(true);
    try {
      await fetchMoreUnplanned({
        variables: { filter: { ...unplannedFilter, offset: have } },
        updateQuery: (prev, { fetchMoreResult }) => {
          const seen = new Set(prev.tasks.items.map((c) => c.id));
          return {
            tasks: {
              ...fetchMoreResult.tasks,
              items: [...prev.tasks.items, ...fetchMoreResult.tasks.items.filter((c) => !seen.has(c.id))],
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMoreUnplanned, unplanned.data, unplannedFilter]);
  const labelsQ = useQuery<{ labels: BoardLabel[] }>(LABELS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const [moveMutation] = useMutation(MOVE_MUTATION);

  const data = board.data?.taskBoard;
  const currentWeek = data?.currentWeek ?? currentWeekKey();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => setNow(Date.now()), [data]);

  // Optimistic overlay: id → the column and index the card was dropped at, until the server answers.
  const [moving, setMoving] = useState<Map<number, { to: ColumnKey; index: number; card: BoardCardData }>>(
    () => new Map(),
  );

  const columns = useMemo(() => {
    const base: Record<ColumnKey, BoardCardData[]> = {
      none: (unplanned.data?.tasks.items ?? []).filter((r) => columnOfWorkflow(r.workflow) === 'none'),
      todo: data?.todo ?? [],
      in_progress: data?.inProgress ?? [],
      done: data?.done ?? [],
    };
    if (!moving.size) return base;
    const out = {} as Record<ColumnKey, BoardCardData[]>;
    for (const k of Object.keys(base) as ColumnKey[]) out[k] = base[k].filter((c) => !moving.has(c.id));
    for (const { to, index, card } of moving.values()) {
      const list = [...out[to]];
      // A reorder keeps the card's "since" chip; a column change starts it over.
      const same = base[to].some((c) => c.id === card.id);
      list.splice(Math.min(index, list.length), 0, same ? card : { ...card, carried: false });
      out[to] = list;
    }
    return out;
  }, [data, unplanned.data, moving]);

  // Every assignee seen this visit, so narrowing the board doesn't empty the picker.
  const seenAssignees = useRef(new Set<string>());
  for (const k of ['todo', 'in_progress', 'done', 'none'] as ColumnKey[]) {
    for (const c of columns[k]) for (const a of c.assignees) seenAssignees.current.add(a.email.toLowerCase());
  }
  const labelNames = useMemo(
    () =>
      (labelsQ.data?.labels ?? [])
        .filter((l) => l.scope !== 'workflow')
        .map((l) => l.name)
        .sort((a, b) => a.localeCompare(b)),
    [labelsQ.data],
  );

  const [toast, setToast] = useState<Toast | null>(null);
  const toastSeq = useRef(0);
  const say = useCallback((tone: Toast['tone'], text: string) => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, tone, text });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(
      () => setToast((cur) => (cur?.id === toast.id ? null : cur)),
      toast.tone === 'bad' ? 9000 : 4000,
    );
    return () => window.clearTimeout(t);
  }, [toast]);

  const { refetch: refetchBoard } = board;
  const { refetch: refetchUnplanned } = unplanned;
  const reload = useCallback(async () => {
    await Promise.all([refetchBoard(), refetchUnplanned()]);
  }, [refetchBoard, refetchUnplanned]);

  const move = useCallback(
    async (card: BoardCardData, from: ColumnKey, plan: DropPlan) => {
      const { to, index } = plan;
      if (plan.unchanged || moving.has(card.id)) return;
      setMoving((m) => new Map(m).set(card.id, { to, index, card }));
      const clear = () =>
        setMoving((m) => {
          const n = new Map(m);
          n.delete(card.id);
          return n;
        });
      try {
        await moveMutation({
          variables: {
            id: card.id,
            to: apiColumn(to),
            expectedFrom: apiColumn(from),
            afterId: plan.afterId,
            beforeId: plan.beforeId,
            week: weekView,
          },
        });
        const what =
          to === 'done' ? ' — closed as done' : from === 'done' ? ' — reopened' : to === 'none' ? ' — off the plan' : '';
        say(
          'ok',
          plan.same
            ? `#${card.id} moved to position ${index + 1} of ${plan.n} in ${columnTitle(to)}.`
            : `#${card.id} moved to ${columnTitle(to)}${what}.`,
        );
        try {
          await reload();
        } finally {
          clear();
        }
      } catch (e) {
        // The reason is what matters here; the transport code is noise on a board.
        const msg = (mutationErrorMessage(e as Error) ?? 'The move failed.').replace(/\s*\([A-Z_]+\)$/, '');
        clear();
        say(
          'bad',
          isConflictError(e, msg)
            ? plan.same
              ? `${columnTitle(to)} changed while you were dragging #${card.id} (someone else moved a card), so it went back and the board reloaded.`
              : `#${card.id} was moved by someone else while you were dragging it, so it went back and the board reloaded.`
            : `#${card.id} can't move to ${columnTitle(to)}: ${msg}`,
        );
        void reload().catch(() => undefined);
      }
    },
    [moving, moveMutation, reload, say, weekView],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: columnKeyboardCoordinates }),
  );
  // The sensors stay the same on every render (dnd-kit keys an effect on them): a past week is
  // read-only through the disabled sortables (no listeners), not by swapping the sensors out.
  const [dragging, setDragging] = useState<{ card: BoardCardData; from: ColumnKey; height: number } | null>(null);
  // Where a card dragged in from ANOTHER column would land (the line between two cards). In its
  // own column the sortable list opens the slot itself, so no line is drawn there.
  const [dropHint, setDropHint] = useState<{ to: ColumnKey; index: number } | null>(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const locate = useCallback((active: Pick<Active, 'data' | 'rect'>, over: Over | null): DropPlan | null => {
    const a = active.data.current as { card?: BoardCardData; from?: ColumnKey } | undefined;
    const to = columnOf(over);
    if (!a?.card || !a.from || !over || !to) return null;
    return planDrop(columnsRef.current, a.card.id, a.from, to, cardIdOf(over.id), isBelow(active, over));
  }, []);
  const announcements = useMemo(() => makeAnnouncements(locate), [locate]);
  const onDragStart = (e: DragStartEvent) => {
    resetKeyboardJump();
    setDropHint(null);
    const a = e.active.data.current as { card?: BoardCardData; from?: ColumnKey } | undefined;
    // dnd-kit measures the active rect only after this event, so read the card's own box (the
    // slot another column opens is this tall).
    const node = a?.card ? document.querySelector(`[data-testid="board-card"][data-id="${a.card.id}"]`) : null;
    const height = node?.getBoundingClientRect().height ?? e.active.rect.current.initial?.height ?? 0;
    setDragging(a?.card && a.from ? { card: a.card, from: a.from, height } : null);
  };
  const onDragMove = (e: DragMoveEvent) => {
    const p = locate(e.active, e.over);
    const next = p && !p.same ? { to: p.to, index: p.index } : null;
    setDropHint((cur) => (cur?.to === next?.to && cur?.index === next?.index ? cur : next));
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    setDropHint(null);
    if (readOnly) return;
    const a = e.active.data.current as { card: BoardCardData; from: ColumnKey } | undefined;
    const plan = locate(e.active, e.over);
    if (a && plan) void move(a.card, a.from, plan);
  };

  const unplannedTotal = unplanned.data?.tasks.total ?? null;
  const unplannedOpen = unplannedPref ?? (unplannedTotal == null || unplannedTotal > 0);
  const toggleUnplanned = () => {
    writePref(!unplannedOpen);
    setUnplannedPref(!unplannedOpen);
  };

  const all = filters.week === 'all';
  const boardWeek = data?.week && data.week !== 'all' ? data.week : filters.week && !all ? filters.week : currentWeek;
  // A past week is a record, not a plan: no dragging until you switch back to this week.
  const readOnly = !all && boardWeek !== currentWeek;
  // Every column has a subtitle so the four headers line up (the grid also aligns them).
  const hint: Record<ColumnKey, string> = {
    none: 'Open tasks without a workflow label. Drag to order; unplaced tasks follow, newest first. Drag one to TODO to plan it.',
    todo: 'Planned, not started yet.',
    in_progress: 'Being worked on.',
    done: all ? 'Moved to done in the last 30 days (at most 100).' : `Moved to done in ${shortWeek(boardWeek)}.`,
  };
  const error = board.error ?? unplanned.error;
  const unplannedLeft = Math.max(0, (unplannedTotal ?? 0) - (unplanned.data?.tasks.items.length ?? 0));

  return (
    <div className="page page--wide board-page" data-testid="board-page">
      <div className="detail-head req-list-head">
        <h2>Board</h2>
        <div style={{ flex: 1 }} />
        <Link to="/tasks/new" className="btn btn-primary">
          + New task
        </Link>
      </div>
      <p className="detail-desc">
        The DevOps / Platform week. Drag a task between columns to change its workflow label; DONE closes it,
        dragging it back out reopens it. Drag it up or down to set its place in the column, for the whole team.
        Weeks run Monday to Sunday, Vietnam time.
      </p>
      <BoardFilters
        value={filters}
        onChange={setFilters}
        currentWeek={currentWeek}
        me={me}
        assignees={[...seenAssignees.current].sort()}
        labels={labelNames}
        labelsFull={labelMode === 'full'}
        onToggleLabels={toggleLabels}
      />
      {error && <p className="error">{mutationErrorMessage(error) ?? error.message}</p>}
      {readOnly && (
        <p className="muted board-readonly" role="note" data-testid="board-readonly">
          Past weeks are read-only — switch to this week to move cards.
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={boardCollision}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragOver={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          setDragging(null);
          setDropHint(null);
        }}
        accessibility={{ announcements, screenReaderInstructions: SR_INSTRUCTIONS }}
      >
        <div
          className={unplannedOpen ? 'board' : 'board board--none-collapsed'}
          data-testid="board"
          aria-busy={board.loading && !data}
        >
          {COLUMNS.map(({ key, title }) => {
            const cards = columns[key];
            const isNone = key === 'none';
            const loadingCol = isNone ? unplanned.loading && !unplanned.data : board.loading && !data;
            // A card from another column opens a gap where it will land (the same column opens its
            // own slot through the sortable list), so the place shows even under the dragged card.
            const slotHere = !!dragging && dropHint?.to === key;
            const slot = slotHere ? (
              <li
                key="drop-slot"
                className="board-slot"
                data-testid="board-drop-slot"
                aria-hidden="true"
                style={{ height: dragging?.height || undefined }}
              />
            ) : null;
            return (
              <BoardColumn
                key={key}
                column={key}
                title={title}
                count={loadingCol ? null : isNone ? cards.length + unplannedLeft : cards.length}
                hint={hint[key]}
                collapsed={isNone && !unplannedOpen}
                onToggle={isNone ? toggleUnplanned : undefined}
                target={dropHint?.to === key}
              >
                {loadingCol && <li className="board-col__empty muted">Loading…</li>}
                {!loadingCol && cards.length === 0 && !slotHere && <li className="board-col__empty">{EMPTY[key]}</li>}
                <SortableContext
                  id={`sort-${key}`}
                  items={cards.map((c) => `card-${c.id}`)}
                  // The unplanned lane is a grid on medium pages; the workflow columns are lists.
                  strategy={isNone ? rectSortingStrategy : verticalListSortingStrategy}
                  disabled={readOnly}
                >
                  {cards.flatMap((c, i) => [
                    slotHere && dropHint?.index === i ? slot : null,
                    <BoardCard
                      key={c.id}
                      card={c}
                      column={key}
                      now={now}
                      labelMode={labelMode}
                      pending={moving.has(c.id)}
                      readOnly={readOnly}
                      drop={
                        dropHint?.to !== key
                          ? undefined
                          : dropHint.index === i
                            ? 'before'
                            : dropHint.index === cards.length && i === cards.length - 1
                              ? 'after'
                              : undefined
                      }
                    />,
                  ])}
                  {slotHere && (dropHint?.index ?? 0) >= cards.length ? slot : null}
                </SortableContext>
                {isNone && !loadingCol && unplannedLeft > 0 && (
                  <li className="board-col__more">
                    <button
                      type="button"
                      className="btn btn-sm"
                      data-testid="board-unplanned-more"
                      disabled={loadingMore}
                      onClick={() => void loadMoreUnplanned()}
                    >
                      {loadingMore ? 'Loading…' : `Load ${Math.min(unplannedLeft, UNPLANNED_PAGE)} more`}
                    </button>
                    <span className="muted">{unplannedLeft} not shown</span>
                  </li>
                )}
              </BoardColumn>
            );
          })}
        </div>
        <DragOverlay dropAnimation={null}>
          {dragging ? (
            // Laid out like a card of the column it came from (the column's narrow-width rules
            // key on data-column), so it looks the same in the hand as in the list.
            <div className="board-card board-card--overlay" data-column={dragging.from}>
              <BoardCardBody card={dragging.card} now={now} labelMode={labelMode} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {toast && (
        <div
          className={`board-toast board-toast--${toast.tone}`}
          role={toast.tone === 'bad' ? 'alert' : 'status'}
          data-testid="board-toast"
        >
          <span>{toast.text}</span>
          <button type="button" className="mini-btn mini-btn--icon" aria-label="Dismiss" onClick={() => setToast(null)}>
            ×
          </button>
        </div>
      )}
    </div>
  );
}
